import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';

import axiosMain from '../../../services';
import { CURRENCY_BY_COUNTRY } from '../../../utils/currency';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_IMAGE_BYTES = 2 * 1024 * 1024;
const useStyles = makeStyles((theme) => ({
  tabPanel: {
    paddingTop: theme.spacing(1),
  },
  pwSection: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(2.5),
    borderRadius: 10,
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,
  },
}));

export default function ProfileTab() {
  const classes = useStyles();
  const history = useHistory();
  const [emailValue, setEmailValue] = useState('');
  const [usernameValue, setUsernameValue] = useState('');
  const [contactValue, setContactValue] = useState('');
  const [countryValue, setCountryValue] = useState('');
  const [addressValue, setAddressValue] = useState('');
  const [cityValue, setCityValue] = useState('');
  const [postalCodeValue, setPostalCodeValue] = useState('');
  const [errValue, setErrValue] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [dpValue, setDp] = useState(null);
  const [fileName, setFileName] = useState('');

  // Change password state
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwChanging, setPwChanging] = useState(false);

  useEffect(() => {
    axiosMain
      .get('/user/view')
      .then((response) => {
        if (response.status === 200) {
          const d = response.data;
          setUsernameValue(d.username || '');
          setEmailValue(d.email || '');
          setContactValue(d.number || '');
          setAddressValue(d.address || '');
          setCountryValue(d.country || '');
          setPostalCodeValue(d.postalCode || '');
          setCityValue(d.city || '');
          const pic = d.displayPicture != null ? d.displayPicture : null;
          setDp(pic);
          try {
            localStorage.setItem('dp', JSON.stringify(pic == null ? null : pic));
          } catch (_) {
            /* ignore */
          }
        }
      })
      .catch(() => {
        setErrValue('Could not load your profile.');
      });
  }, []);

  const handleUpdate = (e) => {
    e.preventDefault();
    setErrValue('');
    const errs = {};
    if (!usernameValue || usernameValue.trim().length < 2) {
      errs.username = 'Name must be at least 2 characters.';
    }
    if (!emailValue || !EMAIL_RE.test(emailValue.trim())) {
      errs.email = 'Enter a valid email address.';
    }
    if (contactValue && contactValue.replace(/\D/g, '').length < 7) {
      errs.contact = 'Enter a valid contact number (at least 7 digits).';
    }
    if (postalCodeValue && !/^\d{4,10}$/.test(postalCodeValue.trim())) {
      errs.postalCode = 'Enter a valid postal code (digits only).';
    }
    if (Object.keys(errs).length) {
      setFieldErrors(errs);
      toast.error('Please fix the highlighted fields.');
      return;
    }
    setFieldErrors({});
    const payload = {
      username: usernameValue,
      email: emailValue,
      number: contactValue,
      address: addressValue,
      country: countryValue,
      currency: CURRENCY_BY_COUNTRY[countryValue] || 'INR',
      city: cityValue,
      postalCode: postalCodeValue,
      displayPicture: dpValue != null && dpValue !== '' ? dpValue : null,
    };
    axiosMain
      .post('/user/update', payload)
      .then((response) => {
        if (response.status === 200) {
          toast.success('Profile updated.');
          try {
            localStorage.setItem('dp', JSON.stringify(dpValue != null && dpValue !== '' ? dpValue : null));
            const userData = JSON.parse(localStorage.getItem('userData') || '{}');
            userData.country = payload.country;
            userData.currency = payload.currency;
            localStorage.setItem('userData', JSON.stringify(userData));
          } catch (_) {
            /* ignore */
          }
          window.location.reload();
        }
      })
      .catch((error) => {
        const msg = error?.response?.data?.message || 'Update failed.';
        setErrValue(msg);
        toast.error(msg);
      });
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!oldPassword || !newPassword || !confirmPassword) {
      toast.error('All password fields are required.');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match.');
      return;
    }
    if (newPassword.length < 8) {
      toast.error('New password must be at least 8 characters.');
      return;
    }
    setPwChanging(true);
    try {
      await axiosMain.post('/user/change-password', { oldPassword, newPassword });
      toast.success('Password changed successfully.');
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to change password.');
    } finally {
      setPwChanging(false);
    }
  };

  const onImageChange = (event) => {
    if (event.target.files && event.target.files[0]) {
      const f = event.target.files[0];
      if (f.size > MAX_IMAGE_BYTES) {
        toast.error('Image must be under 2 MB. Please choose a smaller file.');
        event.target.value = '';
        return;
      }
      if (!f.type.startsWith('image/')) {
        toast.error('Only image files are accepted (JPG, PNG, GIF, etc.).');
        event.target.value = '';
        return;
      }
      setFileName(f.name);
      const reader = new FileReader();
      reader.onload = (ev) => {
        setDp(ev.target.result);
      };
      reader.readAsDataURL(f);
    }
  };

  return (
    <div className={classes.tabPanel}>
      {errValue ? (
        <Typography color="error" variant="body2" paragraph>
          {errValue}
        </Typography>
      ) : null}
      <div className="card">
        <div className="card-header">
          <h4 className="card-title">Profile</h4>
        </div>
        <div className="card-body">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleUpdate(e);
            }}
          >
            <div className="row g-4">
              <div className="col-xxl-6 col-xl-6">
                <label className="form-label">Your name <span className="text-danger">*</span></label>
                <input
                  type="text"
                  className={`form-control${fieldErrors.username ? ' is-invalid' : ''}`}
                  placeholder="Name"
                  value={usernameValue}
                  onChange={(e) => { setUsernameValue(e.target.value); if (fieldErrors.username) setFieldErrors((p) => ({ ...p, username: '' })); }}
                />
                {fieldErrors.username && <div className="invalid-feedback">{fieldErrors.username}</div>}
              </div>
              <div className="col-xxl-6 col-xl-6">
                <label className="form-label">Email <span className="text-danger">*</span></label>
                <input
                  type="email"
                  className={`form-control${fieldErrors.email ? ' is-invalid' : ''}`}
                  placeholder="Email"
                  value={emailValue}
                  onChange={(e) => { setEmailValue(e.target.value); if (fieldErrors.email) setFieldErrors((p) => ({ ...p, email: '' })); }}
                />
                {fieldErrors.email && <div className="invalid-feedback">{fieldErrors.email}</div>}
              </div>
              <div className="col-xxl-12">
                <label className="form-label">Profile photo</label>
                <div className="d-flex align-items-center flex-wrap" style={{ gap: 12 }}>
                  {dpValue ? (
                    <img
                      className="rounded-circle"
                      src={dpValue}
                      width="72"
                      height="72"
                      alt=""
                      style={{ objectFit: 'cover' }}
                    />
                  ) : (
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center"
                      style={{ width: 72, height: 72, background: 'rgba(218,46,9,0.1)', border: '2px dashed rgba(218,46,9,0.3)' }}
                    >
                      <i className="icofont-user" style={{ fontSize: 28, color: '#DA2E09', opacity: 0.6 }} />
                    </div>
                  )}
                  <div className="form-file">
                    <input
                      type="file"
                      accept="image/*"
                      className="form-file-input"
                      id="settingsProfilePhoto"
                      onChange={onImageChange}
                    />
                    <label className="form-file-label" htmlFor="settingsProfilePhoto">
                      <span className="form-file-text">{fileName || 'Choose image…'}</span>
                      <span className="form-file-button">Browse</span>
                    </label>
                  </div>
                </div>
                <p className="text-muted small mt-2 mb-0">Upload a photo to show it here.</p>
              </div>
              <div className="col-xxl-6 col-xl-6">
                <label className="form-label">Contact number</label>
                <input
                  type="text"
                  className={`form-control${fieldErrors.contact ? ' is-invalid' : ''}`}
                  value={contactValue}
                  onChange={(e) => { setContactValue(e.target.value); if (fieldErrors.contact) setFieldErrors((p) => ({ ...p, contact: '' })); }}
                />
                {fieldErrors.contact && <div className="invalid-feedback">{fieldErrors.contact}</div>}
              </div>
              <div className="col-xxl-12">
                <label className="form-label">Address</label>
                <input
                  type="text"
                  className="form-control"
                  value={addressValue}
                  onChange={(e) => setAddressValue(e.target.value)}
                />
              </div>
              <div className="col-xxl-4 col-xl-4">
                <label className="form-label">City</label>
                <input
                  type="text"
                  className="form-control"
                  value={cityValue}
                  onChange={(e) => setCityValue(e.target.value)}
                />
              </div>
              <div className="col-xxl-4 col-xl-4">
                <label className="form-label">Postal code</label>
                <input
                  type="text"
                  className={`form-control${fieldErrors.postalCode ? ' is-invalid' : ''}`}
                  value={postalCodeValue}
                  onChange={(e) => { setPostalCodeValue(e.target.value); if (fieldErrors.postalCode) setFieldErrors((p) => ({ ...p, postalCode: '' })); }}
                />
                {fieldErrors.postalCode && <div className="invalid-feedback">{fieldErrors.postalCode}</div>}
              </div>
              <div className="col-xxl-4 col-xl-4">
                <label className="form-label">Country</label>
                <select
                  className="form-control"
                  value={countryValue in CURRENCY_BY_COUNTRY ? countryValue : 'India'}
                  onChange={(e) => setCountryValue(e.target.value)}
                >
                  <option value="India">India</option>
                  <option value="USA">USA</option>
                </select>
                <small className="text-muted">
                  Billing currency: {CURRENCY_BY_COUNTRY[countryValue] || 'INR'}
                </small>
              </div>
              <div className="col-12">
                <button type="submit" className="btn btn-success pl-5 pr-5 waves-effect">
                  Save changes
                </button>
                <button type="button" className="btn btn-outline-secondary ml-2" onClick={() => history.push('/')}>
                  Back to dashboard
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* Change password */}
      <div className={classes.pwSection}>
        <Typography variant="subtitle1" style={{ fontWeight: 700, marginBottom: 16 }}>
          Change password
        </Typography>
        <form onSubmit={handleChangePassword}>
          <div className="row g-3">
            <div className="col-xxl-4 col-xl-4 col-12">
              <label className="form-label">Current password</label>
              <input
                type="password"
                className="form-control"
                value={oldPassword}
                onChange={(e) => setOldPassword(e.target.value)}
                autoComplete="current-password"
              />
            </div>
            <div className="col-xxl-4 col-xl-4 col-12">
              <label className="form-label">New password</label>
              <input
                type="password"
                className="form-control"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
              />
            </div>
            <div className="col-xxl-4 col-xl-4 col-12">
              <label className="form-label">Confirm new password</label>
              <input
                type="password"
                className="form-control"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
              />
            </div>
            <div className="col-12">
              <button
                type="submit"
                className="btn btn-primary pl-4 pr-4 waves-effect"
                disabled={pwChanging}
              >
                {pwChanging ? 'Updating…' : 'Update password'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
